import { Profile, PublicationCardItem } from '@news-mono/web-common'
import { ProfileV4DTO } from '@west-australian-newspapers/publication-types'
import React, { Fragment } from 'react'
import { useTheme } from '@emotion/react'

import {
    StyledArticleByline,
    StyledArticleBylineAuthor,
    StyledArticleBylineAuthorLink,
    StyledArticleBylineAuthorDelimiter,
    StyledAuthorProfilesWrap,
    StyledAuthorThumbnail,
    StyledTimeAndProfilesWrap,
} from './ByLine.styled'
import { reorderAuthors } from './reorder-authors'

const authorImageDefault = require('./assets/author-image.jpg')
const authorImageSevenYou = require('./assets/author-image-7you.jpg')

const viewImage = require('./assets/view-logo-profile.png')
const nbcImage = require('./assets/nbc.svg')
const appImage = require('./assets/aap.svg')
const cnnImage = require('./assets/cnn.svg')
const apImage = require('./assets/ap.svg')
const theConversationImage = require('./assets/conversation.svg')
const theNightlyImage = require('./assets/nightly.svg')

const renderProfilePill = (
    profile: Profile,
    source: string,
    byline: string | undefined,
    authorDefaultImage: string,
) => {
    return (
        <>
            {profile.thumbnailPhoto ? (
                <AuthorProfileLink profile={profile}>
                    <StyledAuthorThumbnail
                        isLast={true}
                        src={profile.thumbnailPhoto}
                        alt={profile.name}
                    />
                </AuthorProfileLink>
            ) : (
                <StyledAuthorThumbnail
                    isLast={true}
                    src={getThumbnail(source, authorDefaultImage)}
                    alt={profile.name}
                />
            )}
            <AuthorProfileLink profile={profile}>
                {byline ? (
                    `By ${byline}`
                ) : (
                    <span itemProp="name">By {profile.name}</span>
                )}
            </AuthorProfileLink>
        </>
    )
}

const renderProfilePillWithMoreNames = (
    profiles: Profile[],
    override: string | undefined,
    authorDefaultImage: string,
) => {
    const sortedProfiles = reorderAuthors(profiles, override)

    return (
        <>
            <StyledAuthorThumbnail isLast={true} src={authorDefaultImage} />
            <StyledTimeAndProfilesWrap>
                <StyledAuthorProfilesWrap>
                    {sortedProfiles.map((profile, index) => {
                        const spacer =
                            index === sortedProfiles.length - 2
                                ? '\u00A0and\u00A0'
                                : index === sortedProfiles.length - 1
                                ? null
                                : ',\u00A0'
                        return (
                            <Fragment key={index}>
                                <AuthorProfileLink
                                    profile={profile}
                                    numAuthors={sortedProfiles.length}
                                >
                                    {profile.name}
                                </AuthorProfileLink>
                                {spacer && (
                                    <StyledArticleBylineAuthorDelimiter
                                        $numAuthors={sortedProfiles.length}
                                    >
                                        {spacer}
                                    </StyledArticleBylineAuthorDelimiter>
                                )}
                            </Fragment>
                        )
                    })}
                </StyledAuthorProfilesWrap>
            </StyledTimeAndProfilesWrap>
        </>
    )
}

export interface ByLineProps {
    profiles: ProfileV4DTO[] | Profile[] | undefined
    byline: string | undefined
    source: string
    hasBackground?: boolean
    isLiveBlog?: boolean
    readTimeMinutes?: number | null
}

export function ByLine({
    profiles,
    byline,
    hasBackground,
    isLiveBlog,
    source,
    readTimeMinutes,
}: ByLineProps) {
    const theme = useTheme() as any
    const authorDefaultImage =
        theme?.section === '7you' ? authorImageSevenYou : authorImageDefault

    if (profiles && profiles.length > 0 && profiles.length < 2) {
        return (
            <>
                {profiles.map((profile) => (
                    <StyledArticleByline
                        key={profile.slug}
                        hasBackground={hasBackground}
                        isLiveBlog={isLiveBlog}
                    >
                        {renderProfilePill(
                            profile as Profile,
                            source,
                            byline,
                            authorDefaultImage,
                        )}
                    </StyledArticleByline>
                ))}
            </>
        )
    } else if (profiles && profiles.length >= 2) {
        return (
            <StyledArticleByline hasBackground={hasBackground}>
                {renderProfilePillWithMoreNames(
                    profiles as Profile[],
                    byline,
                    authorDefaultImage,
                )}
            </StyledArticleByline>
        )
    }

    return (
        <StyledArticleByline hasBackground={hasBackground}>
            <StyledAuthorThumbnail
                isLast={true}
                src={getThumbnail(source, authorDefaultImage)}
                alt={byline || ''}
            />
            <StyledArticleBylineAuthor>
                By {String(byline)}
            </StyledArticleBylineAuthor>
        </StyledArticleByline>
    )
}

interface ProfileProp {
    profile: Profile
}
const AuthorProfileLink: React.FC<ProfileProp & { numAuthors?: number }> = ({
    profile,
    numAuthors,
    children,
}) => {
    return (
        <StyledArticleBylineAuthorLink
            to={`/profile/${profile.slug}`}
            $numAuthors={numAuthors}
            itemScope
            itemType="http://schema.org/Person"
        >
            {children}
        </StyledArticleBylineAuthorLink>
    )
}

function getThumbnail(source: string | undefined, authorDefaultImage: string) {
    switch (source) {
        case 'NBC':
            return nbcImage
        case 'CNN':
            return cnnImage
        case 'AP':
            return apImage
        case 'AAP/7NEWS':
        case 'AAP':
            return appImage
        case 'The Conversation':
            return theConversationImage
        case 'The Nightly':
            return theNightlyImage
        case 'view.com.au':
            return viewImage
        default:
            return authorDefaultImage
    }
}
